import { Card, Group, Badge, Text, Stack, ActionIcon, Tooltip } from '@mantine/core';
import { IconDots, IconTrash, IconExternalLink, IconBriefcase, IconCalendar, IconMapPin, IconCoin, IconFileText } from '@tabler/icons-react';
import { Job } from '../../api/jobs';

interface JobCardProps {
  job: Job;
  onDelete?: (id: string) => void;
  onClick?: () => void;
}

const statusColors: Record<string, string> = {
  active: 'yellow',
  expired: 'gray',
  applied: 'green',
  rejected: 'red',
};

const statusLabels: Record<string, string> = {
  active: 'Active',
  expired: 'Expired',
  applied: 'Applied',
  rejected: 'Rejected',
};

export default function JobCard({ job, onDelete, onClick }: JobCardProps) {
  const deadline = job.deadline ? new Date(job.deadline).toLocaleDateString() : 'No deadline';

  return (
    <Card
      p="md"
      radius="md"
      withBorder
      style={{
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#228be6';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '';
        e.currentTarget.style.boxShadow = '';
        e.currentTarget.style.transform = '';
      }}
    >
      <Stack gap="sm" onClick={onClick}>
        {/* Header: Company — Job Title (and Actions) */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text fw={600} size="lg" style={{ lineHeight: 1.2 }}>
            {job.company} — {job.title}
          </Text>

          {/* Direct Action Icons */}
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Open in Browser" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(job.url, '_blank');
                }}
              >
                <IconExternalLink size={18} />
              </ActionIcon>
            </Tooltip>
            {onDelete && (
              <Tooltip label="Delete Job" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(job.id);
                  }}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>

        {/* Metadata Row */}
        <Group gap="lg" wrap="wrap" align="center">
          {/* Status Pill - Now first item on second line */}
          <Badge
            color={statusColors[job.status]}
            variant="filled"
            size="sm"
            tt="uppercase"
          >
            {statusLabels[job.status]}
          </Badge>

          {/* Location */}
          {job.location && (
            <Tooltip label="Location" withArrow>
              <Group gap={4} style={{ cursor: 'default' }}>
                <IconMapPin size={16} color="var(--mantine-color-gray-6)" />
                <Text size="sm" c="dimmed">
                  {job.location}
                </Text>
              </Group>
            </Tooltip>
          )}

          {/* Salary */}
          {job.salary && (
            <Tooltip label="Salary" withArrow>
              <Group gap={4} style={{ cursor: 'default' }}>
                <IconCoin size={16} color="var(--mantine-color-gray-6)" />
                <Text size="sm" c="dimmed">
                  {job.salary}
                </Text>
              </Group>
            </Tooltip>
          )}

          {/* Timestamps */}
          <Group gap={0} wrap="nowrap">
            <Text size="sm" c="dimmed" mx={6}>•</Text>
            <Tooltip label="Added" withArrow>
              <Group gap={4} style={{ cursor: 'default' }}>
                <IconCalendar size={16} color="var(--mantine-color-gray-6)" />
                <Text size="sm" c="dimmed">
                  Added {new Date(job.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </Text>
              </Group>
            </Tooltip>
          </Group>
        </Group>
      </Stack>
    </Card>
  );
}
